import { CaptureRow, FboOrderData } from './group-customer-capture-rate.types';
import { GroupCustomerCaptureRateFboResult } from 'src/app/models/group-customer-capture-rate-fbo-result';
import { 
    PERCENTAGE_MULTIPLIER, 
    PERCENTAGE_DIVISOR,
    COLUMN_PREFIXES 
} from './group-customer-capture-rate.constants';

export class CaptureRateDataTransformer {
    static combineGroupedData(
        groupedData: GroupCustomerCaptureRateFboResult[]
    ): CaptureRow[] {
        const byCustomerId = new Map<number, CaptureRow>();

        for (const fboResult of groupedData || []) {
            for (const item of fboResult.data || []) {
                const customerId = Number(item.customerId);
                const key = customerId || Number(item.oid) || undefined;
                
                if (key === undefined) {
                    continue;
                }

                let row = byCustomerId.get(key);
                if (!row) {
                    row = this.createInitialRow(item, customerId);
                    byCustomerId.set(key, row);
                }

                row.fboOrders[fboResult.fboId] = this.extractFboOrderData(item);
            }
        }

        return this.calculateNetworkCaptureRates(Array.from(byCustomerId.values()));
    }

    private static createInitialRow(
        item: any,
        customerId: number
    ): CaptureRow {
        return {
            oid: Number(item.oid),
            customerId: customerId,
            company: item.company,
            fboOrders: {},
            networkCaptureRate: 0,
            totalOrdersAcrossAllLocations: 0,
            totalOrdersInNetwork: 0,
        };
    }

    private static extractFboOrderData(item: any): FboOrderData {
        return {
            totalOrders: Number(item.totalOrders || 0),
            airportOrders: Number(item.airportOrders || 0),
        };
    }

    private static calculateNetworkCaptureRates(
        customers: CaptureRow[]
    ): CaptureRow[] {
        return customers.map((customer) => {
            const { totalNetworkOrders, totalAirportOrders } =
                this.aggregateFboData(customer.fboOrders);

            customer.totalOrdersInNetwork = totalNetworkOrders;
            customer.totalOrdersAcrossAllLocations = totalAirportOrders;
            customer.networkCaptureRate = this.calculateCaptureRate(
                totalNetworkOrders,
                totalAirportOrders
            );

            return customer;
        });
    }

    private static aggregateFboData(
        fboOrders: CaptureRow['fboOrders']
    ): { totalNetworkOrders: number; totalAirportOrders: number } {
        let totalNetworkOrders = 0;
        let totalAirportOrders = 0;

        for (const fboData of Object.values(fboOrders)) {
            totalNetworkOrders += fboData.totalOrders;
            totalAirportOrders += fboData.airportOrders;
        }

        return { totalNetworkOrders, totalAirportOrders };
    }

    private static calculateCaptureRate(
        totalOrders: number,
        airportOrders: number
    ): number {
        if (airportOrders === 0) {
            return 0;
        }

        return (
            Math.round((totalOrders / airportOrders) * PERCENTAGE_MULTIPLIER) /
            PERCENTAGE_DIVISOR
        );
    }
}

export class ColumnBuilder {
    static createColumnId(prefix: string, fboId: number): string {
        return `${prefix}${fboId}`;
    }

    static createAirportOrdersColumnId(fboId: number): string {
        return this.createColumnId(COLUMN_PREFIXES.AIRPORT_ORDERS, fboId);
    }

    static createFboOrdersColumnId(fboId: number): string {
        return this.createColumnId(COLUMN_PREFIXES.FBO_ORDERS, fboId);
    }
}

export class SortHelper {
    static getPropertyValue(item: any, property: string): any {
        const properties = property.split('.');
        return properties.reduce((obj, prop) => obj && obj[prop], item);
    }

    static compareValues(
        valueA: any,
        valueB: any,
        direction: string
    ): number {
        if (valueA < valueB) {
            return direction === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
            return direction === 'asc' ? 1 : -1;
        }
        return 0;
    }
}


